import { Zap, CheckCircle2, ArrowRight, Sparkles, Shield, TrendingUp, Target, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";

const WebsiteAEO = () => {
    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-destructive/20 rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
            </div>

            {/* Header */}
            <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <Link to="/">
                        <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
                    </Link>
                    <nav className="hidden md:flex gap-6">
                        <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
                            Home
                        </Link>
                        <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
                            Contato
                        </Link>
                    </nav>
                    <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
                        <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
                    </Button>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-20 md:py-32 px-4 relative">
                <div className="container mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
                            <ArrowRight className="h-4 w-4 rotate-180" />
                            Voltar para Home
                        </Link>

                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 border border-destructive/20 rounded-full mb-6 backdrop-blur-sm">
                            <Zap className="h-4 w-4 text-destructive animate-pulse" />
                            <span className="text-sm font-medium text-destructive">ALERTA CRÍTICO</span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                            O Fim dos Sites{" "}
                            <span className="text-muted-foreground line-through">"Apenas Bonitos"</span>.
                            <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                                O Início dos Ativos Digitais.
                            </span>
                        </h1>

                        <h2 className="text-xl md:text-2xl text-muted-foreground mb-6">
                            Seu site está preparado para a era da Inteligência Artificial?
                        </h2>

                        <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
                            Seu site não pode mais ser um simples cartão de visitas digital. Ele precisa ser o{" "}
                            <strong className="text-primary">motor de vendas</strong> da sua empresa e a{" "}
                            <strong className="text-primary">fonte principal de respostas</strong> para buscadores e IAs.
                        </p>

                        <Button size="lg" asChild className="gap-2 group">
                            <Link to="/diagnostico-gratuito">
                                Diagnóstico Gratuito <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Problem Section */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto relative z-10">
                    <div className="max-w-5xl mx-auto">
                        <Card className="border-destructive/30 bg-destructive/5 backdrop-blur-sm">
                            <CardHeader>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="h-10 w-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                                        <Zap className="h-5 w-5 text-destructive" />
                                    </div>
                                    <CardTitle className="text-2xl md:text-3xl text-foreground">
                                        O Problema: O "Balde Furado" da Maioria dos Sites
                                    </CardTitle>
                                </div>
                                <CardDescription className="text-base text-muted-foreground">
                                    Muitas empresas investem alto em Google Ads, mas direcionam esse tráfego para sites lentos,
                                    construídos sobre templates genéricos (como WordPress). O resultado é sempre o mesmo:
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <div className="text-5xl font-bold text-destructive">33/100</div>
                                        <h4 className="font-semibold text-foreground text-lg">Performance Crítica</h4>
                                        <p className="text-sm text-muted-foreground">
                                            Notas baixas no Google PageSpeed comprometem sua visibilidade e credibilidade.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-5xl font-bold text-destructive">5seg</div>
                                        <h4 className="font-semibold text-foreground text-lg">Alta Taxa de Abandono</h4>
                                        <p className="text-sm text-muted-foreground">
                                            O cliente clica no anúncio, o site demora e ele desiste antes de ver sua oferta.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-5xl font-bold text-destructive">💸</div>
                                        <h4 className="font-semibold text-foreground text-lg">Dinheiro Desperdiçado</h4>
                                        <p className="text-sm text-muted-foreground">
                                            Você paga pelo clique, mas o lead é perdido pela "fricção" da tecnologia.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Solution Section */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto relative z-10">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4 backdrop-blur-sm">
                                <Sparkles className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium text-primary">A SOLUÇÃO</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
                                Performance como Alicerce, IA como Bússola
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                Nossa abordagem é fundamentalmente diferente. Nós não "reformamos" templates;{" "}
                                <strong className="text-primary">nós construímos ativos digitais de alta performance</strong>.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            {/* Card 1: Performance */}
                            <Card className="border-primary/30 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group">
                                <CardHeader>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Zap className="h-6 w-6 text-primary" />
                                        </div>
                                        <CardTitle className="text-xl">
                                            Desenvolvimento Web Focado em Velocidade
                                        </CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-muted-foreground">
                                        Substituímos a base lenta do WordPress por uma arquitetura moderna (React/Next.js).
                                        Isso não é apenas uma melhoria, é uma reconstrução completa que resulta em:
                                    </p>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                            <div>
                                                <strong className="text-foreground">Carregamento Instantâneo:</strong>
                                                <span className="text-muted-foreground"> Atingimos notas 95+ no Google PageSpeed, eliminando a taxa de abandono por lentidão</span>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                            <div>
                                                <strong className="text-foreground">Design Focado em Conversão:</strong>
                                                <span className="text-muted-foreground"> Criamos páginas no conceito de "Sala Fechada" (Landing Pages), onde o usuário só tem uma opção: converter</span>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                            <div>
                                                <strong className="text-foreground">Segurança Robusta:</strong>
                                                <span className="text-muted-foreground"> Uma arquitetura moderna é inerentemente mais segura contra os ataques comuns que afetam plataformas de templates</span>
                                            </div>
                                        </li>
                                    </ul>
                                </CardContent>
                            </Card>

                            {/* Card 2: AEO */}
                            <Card className="border-primary/30 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group">
                                <CardHeader>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Sparkles className="h-6 w-6 text-primary" />
                                        </div>
                                        <CardTitle className="text-xl">
                                            AEO: Otimização para Respostas de IA
                                        </CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-muted-foreground">
                                        O SEO (otimização para links) evoluiu. O futuro é o{" "}
                                        <strong className="text-primary">AEO (Answer Engine Optimization)</strong>.
                                    </p>
                                    <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                                        <p className="text-sm text-foreground italic mb-3">
                                            "Não otimizamos seu site apenas para aparecer na lista do Google; otimizamos seu site para ser a própria resposta que a Inteligência Artificial dará ao seu cliente."
                                        </p>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">Como fazemos?</strong> Através da implementação correta de Dados Estruturados (Schema.org),
                                        corrigimos a confusão técnica (como H1s duplicados) e "etiquetamos" cada informação.
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">O Resultado:</strong> Quando um usuário perguntar ao Google ou a uma IA
                                        "Qual a melhor empresa de [seu serviço] em [sua cidade]?", a IA usará o seu site como a fonte autoritativa da resposta.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-16 md:py-24 px-4 relative bg-card/20">
                <div className="container mx-auto relative z-10">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                                O que você ganha?
                            </h2>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <Card className="border-primary/30 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105">
                                <CardHeader>
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                                        <Target className="h-6 w-6 text-primary" />
                                    </div>
                                    <CardTitle className="text-lg">
                                        Conversão Máxima
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground">
                                        Um site que transforma cliques de anúncios em clientes reais, eliminando o "balde furado".
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="border-primary/30 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105">
                                <CardHeader>
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                                        <Shield className="h-6 w-6 text-primary" />
                                    </div>
                                    <CardTitle className="text-lg">
                                        Autoridade Digital
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground">
                                        Deixe de ser ignorado pelas IAs e torne-se a fonte de referência no seu segmento.
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="border-primary/30 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105">
                                <CardHeader>
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                                        <TrendingUp className="h-6 w-6 text-primary" />
                                    </div>
                                    <CardTitle className="text-lg">
                                        ROI Máximo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground">
                                        O fim do desperdício e o máximo retorno sobre seu investimento em marketing digital.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-24 md:py-32 px-4 relative">
                <div className="container mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto">
                        <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                            <div className="text-center py-12 md:py-16 px-6 relative z-10">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full mb-6 backdrop-blur-sm">
                                    <Rocket className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium text-primary">COMECE AGORA</span>
                                </div>

                                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
                                    Seu site está lento e perdendo dinheiro?
                                </h2>

                                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                                    Descubra os problemas de performance que estão custando leads e vendas.
                                    Solicite um diagnóstico gratuito e receba um relatório completo.
                                </p>

                                <Button size="lg" asChild className="gap-2 group/btn bg-primary text-primary-foreground hover:bg-primary/90">
                                    <Link to="/diagnostico-gratuito">
                                        <span className="flex items-center gap-2">
                                            Solicitar Diagnóstico Gratuito <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                                        </span>
                                    </Link>
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default WebsiteAEO;
